import React, { useState, useCallback } from "react"
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { FilterData, IndexType } from "../../../components/Extra"
import VendorsTable from "./VendorsTable"
import VendorsGrid from "./VendorsGrid"
import AddNewVendor from "./Modals/AddNewVendor"
import AdjustCommission from "./Modals/AdjustCommission"
import { vendorsData, vendorStats } from "../../../common/data/vendors"
import { Vendor } from "./types"
import { useTranslation } from "react-i18next"

export const Vendors = () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<IndexType>("list-ul")
  const [vendors, setVendors] = useState<Vendor[]>(vendorsData)
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>(vendorsData)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showCommissionModal, setShowCommissionModal] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null)

  const handleToggleStatus = useCallback((vendor: Vendor) => {
    const updatedVendors = vendors.map(v => 
      v.id === vendor.id 
        ? { ...v, status: v.status === "Active" ? "In Active" as const : "Active" as const }
        : v
    )
    setVendors(updatedVendors)
    setFilteredVendors(updatedVendors)
  }, [vendors])

  const handleVerify = useCallback((vendor: Vendor) => {
    console.log("Verify vendor:", vendor.id)
    // TODO: Implement verify logic
  }, [])

  const handleAdjustCommission = useCallback((vendor: Vendor) => {
    setSelectedVendor(vendor)
    setShowCommissionModal(true)
  }, [])

  const handleViewDetails = useCallback((vendor: Vendor) => {
    console.log("View vendor details:", vendor.id)
    // TODO: Navigate to vendor details page
  }, [])

  // Statistics data
  const stats = [
    { label: "vendors_totalVendors", value: vendorStats.totalVendors, icon: "bx-store", color: "primary" },
    { label: "vendors_activeVendors", value: vendorStats.activeVendors, icon: "bx-check-circle", color: "success" },
    { label: "vendors_inActiveVendors", value: vendorStats.inActiveVendors, icon: "bx-x-circle", color: "danger" },
    { label: "vendors_verifications", value: vendorStats.verifications, icon: "bx-badge-check", color: "info" },
    { label: "vendors_zones", value: vendorStats.zones, icon: "bx-map", color: "warning" }
  ]

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs 
          title={t("dashboard_dashboard")} 
          breadcrumbItem={t("vendors_vendors")} 
        />
        
        {/* Statistics Cards */}
        <Row>
          {stats.map((stat, idx) => (
            <Col md="4" lg="2" xl="2" key={idx} className="col-lg-5ths mb-3">
              <Card className="mini-stats-wid shadow-md rounded-3">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-muted fw-medium mb-2">{t(stat.label)}</p>
                      <h4 className="mb-0">{stat.value}</h4>
                    </div>
                    <div className="avatar-sm rounded-circle bg-primary-30 align-self-center">
                      <span className="avatar-title rounded-circle bg-transparent">
                        <i className={`bx ${stat.icon} font-size-24 text-${stat.color}`}></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Search and Actions */}
        <Row className="mb-3">
          <Col lg="12">
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ flex: 1, maxWidth: "600px" }}>
                <FilterData
                  data={vendors}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  bearerFilteredData={setFilteredVendors}
                  showSearchFilterBtn
                />
              </div>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="bg-gradiant-blue border-0 ms-3"
              >
                <i className="bx bx-plus me-1"></i>
                {t("vendors_addNewVendor")}
              </Button>
            </div>
          </Col>
        </Row>

        {/* Table or Grid View */}
        {activeTab === "list-ul" ? (
          <VendorsTable 
            vendors={filteredVendors}
            onToggleStatus={handleToggleStatus}
            onVerify={handleVerify}
            onAdjustCommission={handleAdjustCommission}
            onViewDetails={handleViewDetails}
          />
        ) : (
          <VendorsGrid 
            vendors={filteredVendors}
            onToggleStatus={handleToggleStatus}
            onVerify={handleVerify}
            onAdjustCommission={handleAdjustCommission}
            onViewDetails={handleViewDetails}
          />
        )}

        {/* Modals */}
        <AddNewVendor 
          isOpen={showAddModal} 
          toggle={() => setShowAddModal(false)} 
        />
        
        {selectedVendor && (
          <AdjustCommission
            isOpen={showCommissionModal}
            toggle={() => {
              setShowCommissionModal(false)
              setSelectedVendor(null)
            }}
            vendor={selectedVendor}
          />
        )}
      </Container>
    </div>
  )
}
